import { CompareRounded, DescriptionRounded, History, LinkRounded, ShareRounded, TableChartRounded } from '@mui/icons-material'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { Alert, AlertTitle, Box, Button, Card, CircularProgress, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Stack, Typography, useTheme } from '@mui/material'
import moment from 'moment'
import { TDocumentDefinitions } from 'pdfmake'
import pdfMake from 'pdfmake/build/pdfmake'
import pdfFonts from 'pdfmake/build/vfs_fonts'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import ApiClient from '../../backend/ApiClient'
import FlowApiClient from '../../backend/sub_api_clients/FlowApiClient'
import { COMPILED_CONSTANTS } from '../../CompiledConstants'
import algo from '../../core/Algo'
import { ApprovalStatus } from '../../definitions/Approval'
import { IContract } from '../../definitions/Contract'
import { IProviderExpense } from '../../definitions/ServiceExpense'
import Formatter from '../../helpers/Formatter'
import ContractsManager from '../../managers/ContractsManager'
import { useAppSelector } from '../../store/Hooks'
import { changeRoute } from '../../store/slices/SessionSlice'
import { ChartDataWithUnit } from '../charts/ChartsHelper'
import ReadyToPrintExpenseByTos from '../contract_analytics/ReadyToPrintExpenseByTos'
import ContractAnalytics from '../contract_tabs/ContractAnalytics'
import ContractReports from '../contract_tabs/ContractReports'
import NewContractAnalytics from '../contract_tabs/NewContractAnalytics'
import ProgramAnalytics from '../contract_tabs/ProgramAnalytics'
import TabsHeader, { ITabItem } from '../controls/TabsHeader'
import TimeSelection from '../controls/TimeSelection'
import ContractVersioningModal from '../editor/form_builder/contract_editor/ContractVersioningModal'
import StatusChip from '../flows/StatusChip'
import useCommand from '../hooks/useCommand'
import useInvalidate from '../hooks/useInvalidate'
import useQuery, { buildQueryKey } from '../hooks/useQuery'
import Tile from '../layout/Tile'
import HelixLoader from '../loaders/HelixLoader'
import { OverlayLoader } from '../loaders/OverlayLoader'
import PermissionsWrapper from '../permissions/PermissionsWrapper'
import RoutingManager from '../routing/Routing'
import { snackbar } from '../services/SnackbarService'
import SettlementTable from '../SettlementTable'
import ContractCompareModal from './ContractCompareModal'
import ContractDetailsInfo from './ContractDetailsInfo'
import ContractDetailsInfoModal from './ContractDetailsInfoModal'
import ContractHistory from './ContractHistory'
import SettlementTab from './SettlementTab'
import Workflows from './Workflows'
pdfMake.vfs = pdfFonts.vfs

export default function ContractDetails() {
    const theme = useTheme()
    const dispatch = useDispatch()
    const [openManageContractModal, setOpenManageContractModal] = useState(false)
    const [openCompareModal, setOpenCompareModal] = useState(false)
    const [openContractVersionModal, setOpenContractVersionModal] = useState(false)
    const [settlementEngineInfo, setSettlementEngineInfo] = useState<{ name: string, version: number, status: ApprovalStatus }>(null)
    const invalidateContractsApproval = useInvalidate({ queryKey: buildQueryKey('entity-approval/*') })
    const invalidateContractsVersion = useInvalidate({ queryKey: buildQueryKey('contract-versions/*') })
    const [anchorElMenu, setAnchorElMenu] = useState(null)
    const [rtpChartsContainer, setRtpChartsContainer] = useState<{ status: boolean, data: { totalExpense: ChartDataWithUnit<'bar', number[], string>, providerExpense: IProviderExpense[] } }>(null)
    const [fileCreationLoading, setfileCreationLoading] = useState(false)
    const memoizedRtpCharts = useMemo(() => rtpChartsContainer, [rtpChartsContainer])
    const [menuItems, setMenuItems] = useState(null)
    const [viewMode, setViewMode] = useState<string>('pmpm')

    const getFirstDayOfYear = (year?: number) => {
        const today = new Date()
        let monthStartDate = new Date(Date.UTC(today.getFullYear(), 0, 1))
        if (year) {
            monthStartDate = new Date(year, 0, 1)
        }
        const formatter = new Intl.DateTimeFormat('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        })
        return formatter.format(monthStartDate)
    }

    const getLastDayOfMonth = (date: string) => {
        const pickedDate = new Date(date)
        const monthNumber = pickedDate.getMonth()
        const year = pickedDate.getFullYear()
        const monthName = pickedDate.toLocaleString('en-US', { month: 'long' })
        const monthLastDay = new Date(year, monthNumber, 0).getDate()
        return monthName + ', ' + monthLastDay + ' ' + year
    }

    const today = new Date()

    const [filterStartDate, setFilterStartDate] = useState(new Date(Date.UTC(today.getFullYear(), 0, 1)))
    const [filterEndDate, setFilterEndDate] = useState(new Date())
    const [filterDate, setFilterDate] = useState(getFirstDayOfYear() + ' - ' + getLastDayOfMonth(filterEndDate.toISOString()))
    const [updateDataFlag, setUpdateDataFlag] = useState(false)

    const handleFilterDateChange = (newValue: moment.Moment | null) => {
        const startDateString = getFirstDayOfYear(newValue.year())
        const endDateMoment = moment(newValue).endOf('month').startOf('day')
        const fullRangeDates = `${startDateString} - ${endDateMoment.format('MMM D, YYYY')}`
        const startDate = new Date(startDateString)

        setFilterStartDate(new Date(Date.UTC(startDate.getFullYear(), 0, 1)))
        setFilterEndDate(endDateMoment.toDate())
        setFilterDate(fullRangeDates)
        setUpdateDataFlag(prev => !prev)
    }

    const params = RoutingManager.getParams<{
        contractUuid: string
        selectedTab?: string
    }>(useAppSelector(s => s.session.routeParams))

    // Type: 3 => Program

    const { response: contract, status: getContractStatus, refetch } = useQuery<IContract>({
        queryKey: buildQueryKey(`contract_${params.contractUuid}`),
        queryFn: () => ApiClient.getContract(params.contractUuid),
        cache: false
    })

    const isProgram = contract?.type?.uuid === 'contract-type_program' || contract?.type?.name === 'Program'
    const isDraft = contract?.status?.name === 'Draft'

    const { response: approvalData } = useQuery({
        queryKey: buildQueryKey('entity-approval', contract?.uuid),
        queryFn: async () => {
            if (!contract?.uuid) {
                return null
            }
            return await ApiClient.getEntityApproval(contract.uuid)
        },
        cache: true
    })

    const { status: contractStatus, response: contractInfo } = useQuery({
        queryKey: buildQueryKey('get-contract', contract?.id),
        queryFn: async () => algo.hasVal(contract?.id) ? await ApiClient.getContractInfo(contract?.id) : null
    })

    const { status: schemaStatus, response: schemaResponse } = useQuery({
        queryKey: buildQueryKey('get-schema', contractInfo?.schemaForeignKey),
        queryFn: async () => contractInfo?.schemaForeignKey ? await ApiClient.getContractSchemaByKey(contractInfo.schemaForeignKey) : null
    })

    const { execute: executeAddVersion, status: statusAddVersion } = useCommand({
        commandFn: ({ contractUUID, includeFiles }: { contractUUID: string, includeFiles: boolean }) => ApiClient.addContractVersion(contractUUID, includeFiles),
        onFinish: result => {
            if (result.response) {
                invalidateContractsVersion()
                invalidateContractsApproval()
                snackbar.show({ severity: 'success', text: 'Contract cloned successfully' })
                dispatch(changeRoute({
                    route: 'contract-editor',
                    params: {
                        contractUUID: result.response.uuid,
                        uploadedFiles: result.response.files || []
                    }
                }))
            }
        }
    })

    useEffect(() => {
        if (getContractStatus === 'success' && contract?.settlementRef?.uuid) {
            const call = async () => {
                const response = await FlowApiClient.getSettlementEngineRefInfo(contract.settlementRef.uuid, contract.settlementRef.ancestorKey)
                setSettlementEngineInfo(response)
            }
            call()
        }
    }, [contract, getContractStatus])

    const settlementRef = useMemo(() => {
        return contract?.settlementRef ?? null
    }, [contract])

    const renderDate = () => {
        return (
            <Box display='flex' alignItems='center' justifyContent='flex-end' flex={1}>
                <Box sx={{ marginLeft: theme.spacing(2) }}>
                    <TimeSelection />
                </Box>
            </Box>
        )
    }

    const renderOptions = () => {
        return (
            <Stack direction='row' alignItems='center' justifyContent='flex-end' spacing={2}>
                <PermissionsWrapper permission='contracts.view'>
                    <Button
                        color='success'
                        variant='contained'
                        disabled={false}
                        startIcon={<CompareRounded />}
                        onClick={() => setOpenCompareModal(true)}>
                        Compare
                    </Button>
                </PermissionsWrapper>
                <PermissionsWrapper permission='contracts.view'>
                    <Box display='flex' alignItems='center' gap={0.5}>
                        <Typography>
                            v{contractInfo?.versioning.version}
                        </Typography>
                        <IconButton
                            disabled={contractStatus === 'loading'}
                            onClick={() => setOpenContractVersionModal(true)}>
                            <History style={{ color: theme.palette.primary.main }} />
                        </IconButton>
                    </Box>
                </PermissionsWrapper>
                <PermissionsWrapper permission='contracts.view'>
                    <Button
                        variant='contained'
                        onClick={() => setOpenManageContractModal(true)}
                        startIcon={status === 'loading'
                            ? <CircularProgress sx={{ marginLeft: 1 }} color='inherit' size={theme.spacing(2.5)} />
                            : <OpenInNewIcon sx={{ marginLeft: 1 }} />}>
                        Manage
                    </Button>
                </PermissionsWrapper>
                <ContractVersioningModal
                    open={openContractVersionModal}
                    onClose={() => setOpenContractVersionModal(false)}
                    contract={contractInfo}
                    addVersionLoading={statusAddVersion === 'loading'}
                    onAddVersion={newVersion =>
                        executeAddVersion({
                            contractUUID: newVersion.uuid,
                            includeFiles: false
                        })}

                />
                <ContractDetailsInfoModal
                    open={openManageContractModal}
                    onAddVersion={async (contract, includePdfs) => {
                        await executeAddVersion({ contractUUID: contract.uuid, includeFiles: includePdfs })
                    }}
                    onClose={() => setOpenManageContractModal(false)}
                    contract={contractInfo}
                    approval={approvalData}
                    cloneStatus={statusAddVersion}
                    schemaName={schemaResponse?.name}
                />
                <ContractCompareModal
                    contractData={contract}
                    onClose={() => setOpenCompareModal(false)}
                    open={openCompareModal}
                />
            </Stack>
        )
    }

    const getLogoFile = async (fileKey: string) => {
        try {
            const response = await ApiClient.readMediaItem(fileKey, COMPILED_CONSTANTS.evolvemediaBucketName)

            const data = await fetch(response.url)
            const blob = await data.blob()
            return new Promise(resolve => {
                const reader = new FileReader()
                reader.readAsDataURL(blob)
                reader.onloadend = () => resolve(reader.result)
            })
        } catch (error) {
            console.error('ERROR: ', error)
        }
    }

    const generatePdfBase64 = useCallback(async (content: {
        label: string
        chart: string
    }[]) => {
        const createRows = rowsContent => {
            return rowsContent.map(row => {
                return [
                    {
                        text: row.label,
                        fontSize: 8,
                        margin: [0, 10, 5, 5]
                    },
                    {
                        image: row.chart,
                        width: 390
                    }
                ]
            })
        }
        const contract = await ApiClient.getContract(params.contractUuid)
        const logo = await getLogoFile('BCBS_Michigan-800x419-1.png-1abc506e-55aa-41a6-a8d5-bf8e8831cffe.png')
        const file: TDocumentDefinitions = {
            defaultStyle: {
                fontSize: 10
            },
            pageOrientation: 'portrait',
            pageSize: 'A4',
            pageMargins: [40, 90, 40, 60],

            header: function (currentPage, pageCount, pageSize) {
                return {
                    margin: [40, 20, 40, 0],
                    style: 'headerContainer',
                    stack: [
                        {
                            columns: [
                                {
                                    image: logo,
                                    width: 90,
                                    margin: [0, 5, 0, 0]
                                },
                                {
                                    text: contract?.name ?? 'Missing',
                                    style: 'mainTitle',
                                    alignment: 'center',
                                    width: '*',
                                    margin: [0, 20, 0, 0]
                                },
                                {
                                    text: 'Run date: \n' + (Formatter.date(today, 'mm/dd/yyyy') ?? 'Missing'),
                                    style: 'runDate',
                                    alignment: 'right',
                                    width: 'auto',
                                    margin: [0, 12, 0, 0]
                                }
                            ]
                        },
                        {
                            canvas: [
                                {
                                    type: 'line',
                                    x1: 0, y1: 10,
                                    x2: pageSize.width - 80, y2: 10,
                                    lineWidth: 1,
                                    lineColor: '#b0b0b0'
                                }
                            ],
                            margin: [0, 0, 0, 0]
                        }
                    ]
                }
            },
            content: [
                {
                    style: 'metaText',
                    table: {
                        widths: [50, '*'],
                        body: [
                            [{ text: 'LOB:', bold: true }, { text: contract?.productType?.name ?? 'Missing' }],
                            [{ text: 'RBCE:', bold: true }, { text: contract?.rbceMcgEntity ?? 'Missing' }],
                            [{ text: 'Horizon:', bold: true }, { text: new Intl.DateTimeFormat('en-US', {
                                year: 'numeric',
                                month: 'short'
                            }).format(filterEndDate) + ' YTD' }],
                            [{ text: 'Age Group:', bold: true }, { text: 'All members' }]
                        ]
                    },
                    layout: 'noBorders'
                },
                {
                    canvas: [
                        {
                            type: 'line',
                            x1: 0, y1: 10,
                            x2: 600 - 80, y2: 10,
                            lineWidth: 1,
                            lineColor: '#b0b0b0'
                        }
                    ],
                    margin: [0, 5, 0, 5]
                },
                {
                    table: {
                        widths: [120, 320],
                        body: createRows(content),
                        style: 'mainContent'
                    },
                    layout: 'noBorders',
                    margin: [0, 0, 0, 0]
                }
            ],
            styles: {
                tableHeader: {
                    bold: true,
                    fontSize: 11,
                    color: 'black'
                },
                headerContainer: {
                    fontSize: 10,
                    color: '#000000'
                },
                mainTitle: {
                    fontSize: 14,
                    bold: true,
                    color: '#000000'
                },
                runDate: {
                    fontSize: 8,
                    bold: true
                },
                metaText: {
                    fontSize: 8,
                    lineHeight: 1,
                    margin: [0, 0, 0, 0]
                }
            }
        }
        pdfMake.createPdf(file).download(`ExpenseByTos - ${new Date().toISOString()}.pdf`)
        setfileCreationLoading(false)
        setRtpChartsContainer(null)
    }, [])

    const handleCsvGeneration = async (fileType: 'csv' | 'pdf', source?: 'expenseByTos' | 'smallCostUtilization' | 'costUtilization', pmpm?: boolean) => {
        setfileCreationLoading(true)
        const result = await ContractsManager.prepareFileDownload(
            fileType,
            contract.id,
            filterStartDate.toISOString().slice(0, 7),
            filterEndDate.toISOString().slice(0, 7),
            source,
            pmpm
        )
        setfileCreationLoading(result)
    }

    const handlePdfGeneration = async (pmpm?: boolean) => {
        setfileCreationLoading(true)
        const result = async () => {
            try {
                const [totalExpense, providerExpense] = await Promise.all([
                    pmpm
                        ? ApiClient.getContractTotalExpensePMPMByPeriod(
                            contract.id,
                            filterStartDate.toISOString().slice(0, 7),
                            filterEndDate.toISOString().slice(0, 7)
                        )
                        : ApiClient.getContractTotalExpenseByPeriod(
                            contract.id,
                            filterStartDate.toISOString().slice(0, 7),
                            filterEndDate.toISOString().slice(0, 7)
                        ),
                    pmpm
                        ? ApiClient.getContractExpensesPMPMGroupedByProvider(
                            contract.id,
                            filterStartDate.toISOString().slice(0, 7),
                            filterEndDate.toISOString().slice(0, 7)
                        )
                        : ApiClient.getContractExpensesGroupedByProvider(
                            contract.id,
                            filterStartDate.toISOString().slice(0, 7),
                            filterEndDate.toISOString().slice(0, 7)
                        )
                ])

                return {
                    totalExpense: {
                        datasets: [{
                            data: totalExpense.map(item => item.value),
                            unit: '$',
                            backgroundColor: [theme.palette.info.main, theme.palette.primary.light, theme.palette.primary.main, theme.palette.secondary.main, theme.palette.primary.dark]
                        }],
                        labels: totalExpense.map(item => item.name)
                    },
                    providerExpense
                }
            } catch (error) {
                console.error('Errore while fetching charts data:', error)
            }
        }
        const chartsData = await result()
        chartsData?.totalExpense?.datasets?.map(el => el.backgroundColor = ['#1F6272', '#248E9A', '#00555A', '#00CDBA', '#013A3B'])
        setRtpChartsContainer({
            status: true,
            data: {
                totalExpense: chartsData.totalExpense,
                providerExpense: chartsData.providerExpense.items
            }
        })
    }

    const renderExportMenu = () => {
        if (!menuItems) return
        return (
            <>
                <Button disabled={getContractStatus === 'loading' || contractStatus === 'loading'} variant='contained' sx={{ textTransform: 'capitalize' }} startIcon={<ShareRounded></ShareRounded>} onClick={e => setAnchorElMenu(e.currentTarget)}>
                    Export
                </Button>

                { Boolean(anchorElMenu) && (
                    <Menu
                        anchorEl={anchorElMenu}
                        open={Boolean(anchorElMenu)}
                        onClose={() => setAnchorElMenu(null)}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'left'
                        }}
                        slotProps={{ paper: { sx: { marginTop: 0.5 } } }}>

                        {
                            menuItems.includes('csv') && (
                                <MenuItem onClick={() => {
                                    setAnchorElMenu(null)
                                    handleCsvGeneration('csv', 'costUtilization')
                                }}>
                                    <ListItemIcon>
                                        <TableChartRounded />
                                    </ListItemIcon>
                                    <ListItemText>Export as CSV</ListItemText>
                                </MenuItem>
                            )
                        }

                        {
                            menuItems.includes('pdf') && (
                                <MenuItem onClick={() => {
                                    handlePdfGeneration(viewMode === 'pmpm')
                                    setAnchorElMenu(null)
                                }}>
                                    <ListItemIcon>
                                        <DescriptionRounded />
                                    </ListItemIcon>
                                    <ListItemText>Export as PDF</ListItemText>
                                </MenuItem>
                            )
                        }

                        {
                            menuItems.includes('link') && (
                                <MenuItem onClick={() => {
                                    setAnchorElMenu(null)
                                    snackbar.show({
                                        severity: 'info',
                                        text: 'This functionality will be developed in future.'
                                    })
                                }}>
                                    <ListItemIcon>
                                        <LinkRounded />
                                    </ListItemIcon>
                                    <ListItemText>Copy link</ListItemText>
                                </MenuItem>
                            )
                        }

                    </Menu>
                ) }
            </>
        )
    }

    const tabs: ITabItem[] = [
        {
            id: 'analytics',
            name: 'Analytics',
            rightRender: renderDate(),
            renderPanel: () => getContractStatus === 'success'
                ? (isProgram ? <ProgramAnalytics contractId={contract?.id} /> : <ContractAnalytics contractId={contract?.id} />)
                : (
                        <Stack
                            alignItems='center'
                            justifyContent='center'
                            height='70vh'>
                            <HelixLoader size={64} />
                        </Stack>
                    )
        },
        {
            id: 'reports',
            name: 'Reports',
            renderPanel: () => <ContractReports contractId={contract.id} />
        },
        {
            id: 'settlements',
            name: 'Settlements',
            rightRender: (
                <PermissionsWrapper permission='settlement-details.view'>
                    <SettlementTab contractUUID={contract?.uuid ?? null} settlementRef={settlementRef} approvalStatus={settlementEngineInfo?.status ?? null} refetch={refetch} />
                </PermissionsWrapper>
            ),
            renderPanel: () => (
                <Stack spacing={2}>
                    {settlementEngineInfo
                        ? (
                                <Card sx={{ padding: 3 }}>
                                    <Stack spacing={3}>
                                        <Typography variant='subtitle1' fontWeight={700}>Chosen engine info</Typography>
                                        <Stack spacing={1}>
                                            <Stack flexDirection='row' alignItems='center'>
                                                <Typography variant='subtitle2' fontWeight={500} color={theme => theme.palette.text.secondary} width='40%'>Engine name:</Typography>
                                                <Typography variant='subtitle2' fontWeight={500} color={theme => theme.palette.text.secondary} width='20%'>Engine version:</Typography>
                                                <Typography variant='subtitle2' fontWeight={500} color={theme => theme.palette.text.secondary} width='20%'>Status:</Typography>
                                                <Typography variant='subtitle2' fontWeight={500} color={theme => theme.palette.text.secondary} width='20%'># of Globals:</Typography>
                                            </Stack>
                                            <Stack flexDirection='row' alignItems='center'>
                                                <Typography variant='body2' width='40%'>{settlementEngineInfo.name}</Typography>
                                                <Typography variant='body2' width='20%'>{settlementEngineInfo.version}</Typography>
                                                <Box width='20%'>
                                                    <StatusChip status={settlementEngineInfo.status} />
                                                </Box>
                                                <Typography variant='body2' width='20%'>{contract?.settlementRef?.globals && Object.keys(contract.settlementRef.globals).length - 2}</Typography>
                                            </Stack>
                                        </Stack>
                                    </Stack>
                                </Card>
                            )
                        : (
                                <Alert severity='info'>
                                    <AlertTitle>To Start a Settlement Engine</AlertTitle>
                                    Set the Settlement Engine associated to this contract on the
                                    {' '}
                                    <strong>“Choose Engine</strong>
                                    {' '}
                                    button and execute it
                                </Alert>
                            )}

                    <SettlementTable contractId={contract?.id} />

                </Stack>
            )
        },
        {
            id: 'workflows',
            name: 'Workflows',
            rightRender: renderDate(),
            renderPanel: () => <Workflows contractId={contract.id} />
        },
        {
            id: 'settings',
            name: 'Details',
            rightRender: renderOptions(),
            renderPanel: () => <ContractDetailsInfo contract={contractInfo} contractStatus={contractStatus} schemaStatus={schemaStatus} contractSchema={schemaResponse} />
        },
        {
            id: 'history',
            name: 'History',
            renderPanel: () => <ContractHistory approval={approvalData} />
        },
        {
            id: 'newAnalytics',
            name: 'New Analytics',
            rightRender: renderExportMenu(),
            renderPanel: () => (
                <NewContractAnalytics
                    contractId={contract?.id}
                    visualizedFilterDate={filterDate}
                    filterStartDate={filterStartDate}
                    filterEndDate={filterEndDate}
                    updateDataFlag={updateDataFlag}
                    handleFilterDateChange={handleFilterDateChange}
                    handleCsvGeneration={handleCsvGeneration}
                    setExportMenu={setMenuItems}
                    viewMode={viewMode}
                    setViewMode={setViewMode}
                />
            )
        }
    ]

    return (
        <>
            <Tile.Container>
                <TabsHeader
                    primary={contract?.name ?? ''}
                    secondary={contract?.counterparty?.name ?? ''}
                    chipText={contract?.type?.name}
                    defaultSelectedTab={params.selectedTab ? params.selectedTab : (isDraft ? 'settings' : 'analytics')}
                    tabs={tabs}
                />
                {
                    (memoizedRtpCharts?.data?.totalExpense && memoizedRtpCharts?.data?.providerExpense) && (
                        <ReadyToPrintExpenseByTos
                            totalExpense={memoizedRtpCharts.data.totalExpense}
                            providerExpense={memoizedRtpCharts.data.providerExpense}
                            handlePdfGeneration={generatePdfBase64}
                        />
                    )
                }
            </Tile.Container>
            {
                fileCreationLoading && (
                    <OverlayLoader open={fileCreationLoading} text='File elaboration in progress' />
                )

            }
        </>
    )
}
